import { translation } from '@web/utils/safeTranslation'

// Phase 4 (keystore isolation) — Lattice/GridPlus pairing-input bridge.
//
// `latticePageController` runs the gridplus-sdk manual pairing flow on the page
// and needs two pieces of user input: the device id and (on first pairing) the
// one-time pairing secret shown on the Lattice screen. The controller is a plain
// service (no React), so it cannot render a modal itself. This singleton bridges
// the two: the controller `await`s `requestLatticeDeviceId()` /
// `requestLatticePairingSecret()`, a React modal (`LatticePairingModal`)
// subscribes and renders the styled input, then resolves/cancels the awaiting
// promise. Replaces the original `window.prompt` placeholder.

export type LatticePromptKind = 'deviceId' | 'pairingSecret'

export type LatticePromptRequest = { kind: LatticePromptKind } | null

type Listener = (req: LatticePromptRequest) => void

let listener: Listener | null = null
let pending: { resolve: (v: string) => void; reject: (e: Error) => void } | null = null
let current: LatticePromptRequest = null

// The modal subscribes on mount. Only one modal instance is expected; the latest
// subscriber wins. Immediately replays the current request so a prompt issued
// before the modal mounted is not lost.
export function subscribeLatticePrompt(l: Listener): () => void {
  listener = l
  l(current)
  return () => {
    if (listener === l) listener = null
  }
}

function request(kind: LatticePromptKind): Promise<string> {
  // A new request supersedes any previous unanswered one (sequential flow:
  // deviceId then secret), so reject the stale one to avoid a dangling promise.
  if (pending) pending.reject(new Error('Lattice pairing input superseded.'))

  return new Promise<string>((resolve, reject) => {
    pending = { resolve, reject }
    current = { kind }
    listener?.(current)
  })
}

export const requestLatticeDeviceId = (): Promise<string> => request('deviceId')

export const requestLatticePairingSecret = (): Promise<string> => request('pairingSecret')

export function resolveLatticePrompt(value: string): void {
  const p = pending
  pending = null
  current = null
  listener?.(null)
  p?.resolve(value)
}

export function cancelLatticePrompt(): void {
  const p = pending
  pending = null
  current = null
  listener?.(null)
  p?.reject(new Error(translation.t('Connecting to the Lattice1 was cancelled.')))
}
